import chroma from "chroma-js";
import * as THREE from "three";
import CryptoJS from "crypto-js";
import {
  COLORBAR_RANGE_MODE,
  DEFAULT_COLORBAR_RANGE_MODE,
} from "./colorBarComponent/colorbarConstants";
import { colors } from "../utils/colors";

export function normalize(val, max, min) {
  if (max - min === 0) return 1; // or 0, it's up to you
  return (val - min) / (max - min);
}
export const f = chroma.scale([
  "0a6d0a",
  "137845",
  "07ed7a",
  "82ed07",
  "ede907",
  "ed7607",
  "ed0707",
  "3e0303",
]);
export const formatNumber = (num) => {
  const [whole, fraction] = num.toFixed(2).split(".");
  const wholeWithDots = [...whole]
    .reverse()
    .join("")
    .replace(/(\d{3})/g, "$1.")
    .split("")
    .reverse()
    .join("");

  const cleanedWholeWithDots = wholeWithDots.startsWith(".")
    ? wholeWithDots.substring(1)
    : wholeWithDots;

  return `${cleanedWholeWithDots},${fraction}`;
};

export function formatHeatmapValue(value, digits = 1) {
  if (value == null && value !== 0) return "";
  const num = Number(value);
  if (!Number.isFinite(num)) return "";
  const safeDigits = Math.max(0, Math.min(4, Math.round(Number(digits) || 0)));
  return num.toFixed(safeDigits);
}

function normalizePolygonPoints(polygon) {
  const points = Array.isArray(polygon?.[0])
    ? polygon.map(([x, y, z = 0]) => [x, y, z])
    : polygon?.length
      ? Array.from({ length: polygon.length / 3 }, (_, i) => [
          polygon[i * 3],
          polygon[i * 3 + 1],
          polygon[i * 3 + 2] ?? 0,
        ])
      : [];

  if (points.length > 1) {
    const [fx, fy] = points[0];
    const [lx, ly] = points[points.length - 1];
    if (Math.abs(fx - lx) < 1e-6 && Math.abs(fy - ly) < 1e-6) {
      points.pop();
    }
  }

  return points;
}

function averagePolygonPoints(points) {
  if (!points.length) return [0, 0, 0];
  let x = 0;
  let y = 0;
  let z = 0;
  points.forEach(([px, py, pz]) => {
    x += px;
    y += py;
    z += pz;
  });
  const count = points.length;
  return [x / count, y / count, z / count];
}

function reducePolygonEdges(points, reducer, initialValue) {
  return points.reduce((acc, current, index) => {
    const previous = points[(index + points.length - 1) % points.length];
    return reducer(acc, current, previous);
  }, initialValue);
}

// Shoelace formula
export function getPolygonCentroid(polygon) {
  const points = normalizePolygonPoints(polygon);
  if (!points.length) return [0, 0, 0];
  if (points.length < 3) return averagePolygonPoints(points);

  const { twiceArea, cx, cy } = reducePolygonEdges(
    points,
    (acc, [xi, yi], [xj, yj]) => {
      const cross = xi * yj - xj * yi;
      acc.twiceArea += cross;
      acc.cx += (xi + xj) * cross;
      acc.cy += (yi + yj) * cross;
      return acc;
    },
    { twiceArea: 0, cx: 0, cy: 0 },
  );

  if (Math.abs(twiceArea) < 1e-12) return averagePolygonPoints(points);

  const z = points.reduce((sum, [, , pz = 0]) => sum + pz, 0) / points.length;

  return [cx / (3 * twiceArea), cy / (3 * twiceArea), z];
}

// Shoelace formula
export function getPolygonArea(polygon) {
  const points = normalizePolygonPoints(polygon);
  if (points.length < 3) return 0;

  const twiceArea = reducePolygonEdges(
    points,
    (sum, [xi, yi], [xj, yj]) => sum + xi * yj - xj * yi,
    0,
  );

  return Math.abs(twiceArea) * 0.5;
}

export function getPolygonPoints(polygon) {
  return normalizePolygonPoints(polygon);
}

export const windDirectionScale = chroma.scale([
  "1607ED",
  "07B7ED",
  "07ED7A",
  "82ED07",
  "EDE907",
  "ED7607",
  "ED0707",
  "3e0303",
]);

export const sunHoursScale = chroma.scale([
  "0087FF",
  "416CF5",
  "5D5FEF",
  "825FDE",
  "A95FCB",
  "EF5DA8",
]);

export const sensorPlacementScale = chroma.scale([
  "a70226",
  "ca2326",
  "de3f2e",
  "fba25b",
  "d1e311",
  "45ad5a",
  "219b51",
  "0f8445",
  "006837",
]);

export const windTurbineScale = sensorPlacementScale;

export const roomEnergyDataScale = chroma.scale([
  "07b7ed",
  "07ed7a",
  "07ED7A",
  "82ed07",
  "ede907",
  "ed7607",
  "ed0707",
  "bd0505",
]);

export const daylightFactorScale = chroma.scale(["FF0000", "FFFF00", "00FF00"]);

export const pvYieldScale = chroma.scale([
  "a70226",
  "ca2326",
  "de3f2e",
  "fba25b",
  "d1e311",
  "45ad5a",
  "219b51",
  "0f8445",
  "006837",
]);

export const roomEnergyDataInfoBoxScale = chroma.scale([
  "80ff80",
  "66ff66",
  "1aff1a",
  "00cc00",
  "009900",
  "008000",
  "006600",
  "004d00",
]);

export const NO_VALUE_COLOR = "black";

export function generateColor(value, minv, maxv, colorScale) {
  if (!value && value != 0) return NO_VALUE_COLOR;
  const value1 = normalize(value, maxv, minv);
  const value2 = colorScale(value1);
  const rgb = value2._rgb;
  const color = `rgb(${Math.floor(rgb[0])}, ${Math.floor(rgb[1])}, ${Math.floor(
    rgb[2],
  )})`;
  return color;
}

export const OUTLIER_DECOLOR = "#000";

export function isInColorbarRange(value, min, max) {
  if (value == null && value !== 0) return false;
  const rangeMin = Number(min);
  const rangeMax = Number(max);
  return value >= rangeMin && value <= rangeMax;
}

export function resolveColorbarSettings(storedEntry, rangeDefaults = {}) {
  const fallback = {
    min: rangeDefaults.min,
    max: rangeDefaults.max,
    rangeMode: DEFAULT_COLORBAR_RANGE_MODE,
  };
  if (!storedEntry) return fallback;
  return {
    min: storedEntry.min ?? fallback.min,
    max: storedEntry.max ?? fallback.max,
    rangeMode: storedEntry.rangeMode ?? fallback.rangeMode,
  };
}

export function getRangeModeDisplayColor(
  value,
  { min, max, rangeMode = DEFAULT_COLORBAR_RANGE_MODE, colorScale },
) {
  return getRangeModeDisplayColorFn(value, {
    min,
    max,
    rangeMode,
    getScaledColor: (value) =>
      generateColor(value, Number(min), Number(max), colorScale),
  });
}

export function filterObjectsByColorbarRange(
  objects,
  getValue,
  min,
  max,
  rangeMode,
) {
  if (!objects?.length || rangeMode !== COLORBAR_RANGE_MODE.EXCLUDE) {
    return objects;
  }
  return objects.filter((obj) => isInColorbarRange(getValue(obj), min, max));
}

export function getRangeModeDisplayColorFn(
  value,
  { min, max, rangeMode = DEFAULT_COLORBAR_RANGE_MODE, getScaledColor },
) {
  const color = getScaledColor(value);
  const inRange = isInColorbarRange(value, min, max);

  const defaultResult = { visible: true, color };

  if (inRange) return defaultResult;

  if (rangeMode === COLORBAR_RANGE_MODE.EXCLUDE) {
    return { visible: false, color: null };
  }

  if (rangeMode === COLORBAR_RANGE_MODE.DECOLOR) {
    return { visible: true, color: OUTLIER_DECOLOR };
  }
  return defaultResult;
}

export const roomTypesColors = [
  "red",
  "blue",
  "#00a86b", //Jade
  "yellow",
  "brown",
  "#b200ed", //Violet
  "pink",
  "orange",
  "gray",
  "#fa8072", //Salmon
  "#000080", //Navy
  "#4cbb17", //Kelly
  // "#fffdd0", //Creame
  "#2b1700", //Chocolate
  "#6f2da8", //Grape
  "#ff6fff", //Ultra

  "#008ecc", //Olympic
  "#043927", //Sacramento
  "#6693f5", //Corn Flower
  "#311432", //Eggplant
];

export const getClickedRoomColor = () => colors.clickedObject();
export const getClickedWindowColor = () => colors.clickedObject();
export const getHoveredWindowColor = () => colors.hoveredObject();

export const getRoomTypeColor = (roomType, allRoomTypes) => {
  const colorIndex = allRoomTypes?.indexOf(roomType);
  if (colorIndex !== -1) return roomTypesColors[colorIndex];
};

export const generateMinMax = (valueList, sensorType) => {
  const values = valueList.map((item) => item[sensorType]);
  return {
    minVal: Math.min(...values),
    maxVal: Math.max(...values),
  };
};
export const calculateColor = (value, minValue, maxValue, colorBar) => {
  if (value === 0.621) {
    const normalizedValue = (value - minValue) / (maxValue - minValue);
  }
  const normalizedValue = (value - minValue) / (maxValue - minValue);
  return chroma.scale(colorBar)(normalizedValue).hex();

  // return chroma.scale(colorBar)(normalizedValue).css();
};

export function generateNumbers(min, max, step) {
  const numbers = [];
  for (let i = min; i <= max; i += step) {
    numbers.push(i);
  }
  return numbers;
}

export function calculatePosition(num, min, max) {
  return ((num - min) / (max - min)) * 100;
}
export function generatePolygon(data) {
  return data.map((k) => new THREE.Vector3(k[0], k[1], k[2]));
}

export const getStepForSlider = (maxValue) => {
  const step = maxValue < 1 ? 0.01 : maxValue < 10 ? 0.1 : 1;
  return step;
};

export const getStepForAxis = (
  minValue,
  maxValue,
  minTicks = 8,
  maxTicks = 15,
) => {
  const predefinedSteps = [
    0.001, 0.002, 0.005, 0.01, 0.02, 0.05, 0.1, 0.2, 0.5, 1, 2, 5, 10, 25, 50,
    100, 250, 500, 1000, 2000, 5000, 10000, 25000, 50000, 100000, 250000,
    500000,
  ];
  const range = maxValue - minValue;

  const availableTicksCount = maxTicks - minTicks + 1;
  const ticksNos = [...Array(availableTicksCount).keys()].map(
    (e) => e + minTicks,
  );
  const upperBounds = predefinedSteps.map((step) => {
    for (const tickNo of ticksNos) {
      if (step * tickNo > range) return step * tickNo;
    }
    return null;
  });
  const bestStepIndex = upperBounds.indexOf(
    Math.min(...upperBounds.filter((e) => e !== null)),
  );
  const result = predefinedSteps[bestStepIndex];
  return result;
};

const getAxisValuesForStep = (minValue, maxValue, step) => {
  const result = [];
  const range = maxValue - minValue;
  const roundedMinValue = Math.floor(minValue / step) * step;
  for (let i = 0; i <= Math.ceil(range / step); i++) {
    result.push(roundedMinValue + i * step);
  }
  return result;
};

export const generateAxisValues = (minValue, maxValue, step) => {
  if (minValue === undefined || maxValue === undefined) return;

  const bestStep = step ?? getStepForAxis(minValue, maxValue);
  if (bestStep === undefined || bestStep === null) return;

  return getAxisValuesForStep(minValue, maxValue, bestStep);
};

export const genratePolygon = (data) => {
  var output = [];
  data.forEach((k) => {
    output.push(new THREE.Vector3(k[0], k[1], k[2]));
  });
  return output;
};

const secretPass = "XkhZG4fW2t2W";

export const encryptData = (text) => {
  const data = CryptoJS.AES.encrypt(
    JSON.stringify(text),
    "secretPass",
  ).toString();
  return data;
};
export const decryptData = (text) => {
  const bytes = CryptoJS.AES.decrypt(text, "secretPass");
  const data = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
  return data;
};

export const hasShadingProgression = (project_id, newProjectId) => {
  if (!project_id && !newProjectId) return false;
  let output = false;
  if (project_id) {
    if ([3, 8, 15, 29, 50, 52, 56].includes(Number(project_id))) output = true;
  }
  if (newProjectId) {
    if ([376, 1289].includes(Number(newProjectId))) output = true;
  }
  return output;
};

export const monthsOrder = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export const arraySum = (arr) => {
  return arr?.reduce((total, current) => total + current, 0);
};

export const getIthStepHourAndMinute = (step, timeIndex) => {
  const totalMinutes = timeIndex * step;

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return { hours, minutes };
};

export const getFormattedHourAndMinuteStr = ({ hours, minutes }) => {
  const formattedHour = String(hours).padStart(2, "0");
  const formattedMinute = String(minutes).padStart(2, "0");

  return { hours: formattedHour, minutes: formattedMinute };
};

export const generateTimeArray = (date, step) => {
  const result = [];
  if (!date) return result;
  const [_, month, day] = date.split("-");
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += step) {
      const { hours: formattedHour, minutes: formattedMinute } =
        getFormattedHourAndMinuteStr({ hours: hour, minutes: minute });
      result.push(`${month}-${day} ${formattedHour}:${formattedMinute}`);
    }
  }
  return result;
};

const SCENE_ROTATION = -Math.PI * 0.5;
export const transformToModelSpace = (position) => {
  const vector = new THREE.Vector3(position[0], position[1], position[2]);
  vector.applyAxisAngle(new THREE.Vector3(1, 0, 0), SCENE_ROTATION);
  return [vector.x, vector.y, vector.z];
};
export const transformFromModelSpace = (position) => {
  const vector = new THREE.Vector3(position[0], position[1], position[2]);
  vector.applyAxisAngle(new THREE.Vector3(1, 0, 0), -SCENE_ROTATION);
  return [vector.x, vector.y, vector.z];
};

export const isEventAClick = (event) => {
  return event.delta <= 2;
};

export const unique = (array) => {
  return [...new Set(array)];
};

export const isWindowPreviouslySelected = (windowId, preRayConfigurations) => {
  return preRayConfigurations?.some((config) =>
    config.windows?.map((window) => window.id)?.includes(windowId)
  );
};

export const isArrayEqual = (array1, array2, threshold = 0) => {
  if (!array1?.length || !array2?.length) return false;
  return array1?.every(
    (value, index) => Math.abs(value - array2[index]) <= threshold
  );
};
